<script setup lang="ts">
import {
  tpgIconCircle,
  tpgIconLineSolid,
  tpgIconLineSolidThick,
  tpgIconLineSolidThicker,
  tpgIconLineSolidThickest,
  tpgIconLineSolidThin,
} from '@tpg/grammar-web-icons';
import type { MaybeElementRef } from '@vueuse/core';
import { onClickOutside } from '@vueuse/core';
import type { MaybeRefOrGetter } from 'vue';
import { computed, ref } from 'vue';

import {
  GrammarOverlayCard,
  GrammarRadioButtonGroup,
  GrammarRadioButtonItem,
  GrammarSectionHeader,
} from '@nora-monorepo/grammar-ui';

import {
  DrawToolColor,
  isDrawToolColor,
  isLineStyle,
  isLineWidth,
  LineStyle,
  LineWidth,
} from '#nora-ui/map/map/DrawToolUIManager';
import { useMapStore } from '#nora-ui/map/stores/map-store';

export interface EditingLineTabProps {
  ignoredClickOutsideElements?: MaybeRefOrGetter<(MaybeElementRef | string)[]>;
  mapId: string;
}
const props = withDefaults(defineProps<EditingLineTabProps>(), {
  ignoredClickOutsideElements: () => [],
});

const emit = defineEmits(['close']);
const containerRef = ref(null);
const mapStore = useMapStore();
const map = mapStore.getMapByIdOrThrow(props.mapId);
const drawToolUI = map.drawToolUI;
const lineStyle = drawToolUI.lineStyle;
const lineOpacity = drawToolUI.lineOpacity;
const lineColor = drawToolUI.lineColor;
const lineWidth = drawToolUI.lineWidth;

function onColorChange(hexColor?: string) {
  if (hexColor && isDrawToolColor(hexColor)) {
    lineColor.value = hexColor;
  }
}

function onLineWidthChange(value?: string) {
  if (!value) {
    return;
  }

  const width = Number.parseFloat(value);
  if (Number.isFinite(width) && isLineWidth(width)) {
    lineWidth.value = width;
  }
}

function onLineStyleChange(value?: string) {
  if (value && isLineStyle(value)) {
    lineStyle.value = value;
  }
}

function onOpacityChange(value?: string) {
  if (!value) {
    return;
  }

  const opacity = Number.parseFloat(value);
  if (Number.isFinite(opacity)) {
    lineOpacity.value = opacity;
  }
}

const showLineWidth = computed(() => {
  return drawToolUI.getActiveDrawTool()?.showLineWidth();
});

const showLineStyle = computed(() => {
  return drawToolUI.getActiveDrawTool()?.showLineStyle();
});

onClickOutside(
  containerRef,
  () => {
    emit('close');
  },
  {
    ignore: props.ignoredClickOutsideElements,
  },
);
</script>

<template>
  <div ref="containerRef">
    <GrammarOverlayCard
      label=""
      class="pointer-events-auto max-w-[320px] medium:max-w-auto"
      style="
        --wordsFormsRadioButtonItemRegularUnselectedMainLeftIconOnlyEnabledWidth: 20px;
        --wordsFormsRadioButtonItemRegularUnselectedMainLeftEnabledMinWidth: 20px;
      "
    >
      <div class="p-1">
        <GrammarSectionHeader
          label="Line Color"
          header-type="form-header"
        />
        <GrammarRadioButtonGroup
          :selected-value="lineColor"
          @input="onColorChange"
        >
          <GrammarRadioButtonItem
            v-for="color in Object.values(DrawToolColor)"
            :key="color"
            :icon-template="tpgIconCircle"
            :icon-color="color"
            :value="color"
          />
        </GrammarRadioButtonGroup>
      </div>
      <div
        v-if="showLineWidth"
        class="p-1"
      >
        <GrammarSectionHeader
          label="Thickness"
          header-type="form-header"
        />
        <GrammarRadioButtonGroup
          :selected-value="`${lineWidth}`"
          @input="onLineWidthChange"
        >
          <GrammarRadioButtonItem
            :value="LineWidth.UltraLight.toString()"
            :icon-template="tpgIconLineSolidThin"
          />
          <GrammarRadioButtonItem
            :value="LineWidth.Light.toString()"
            :icon-template="tpgIconLineSolid"
          />
          <GrammarRadioButtonItem
            :value="LineWidth.Normal.toString()"
            :icon-template="tpgIconLineSolidThick"
          />
          <GrammarRadioButtonItem
            :value="LineWidth.Medium.toString()"
            :icon-template="tpgIconLineSolidThicker"
          />
          <GrammarRadioButtonItem
            :value="LineWidth.Bold.toString()"
            :icon-template="tpgIconLineSolidThickest"
          />
        </GrammarRadioButtonGroup>
      </div>
      <div
        v-if="showLineStyle"
        class="p-1"
      >
        <GrammarSectionHeader
          label="Style"
          header-type="form-header"
        />
        <GrammarRadioButtonGroup
          :selected-value="lineStyle"
          @input="onLineStyleChange"
        >
          <GrammarRadioButtonItem
            v-for="style in [LineStyle.NoLine, LineStyle.Solid]"
            :key="style"
            :label="style === LineStyle.NoLine ? 'No line' : 'Solid line'"
            :value="style"
          />
        </GrammarRadioButtonGroup>
      </div>
      <div class="p-1">
        <GrammarSectionHeader
          label="Opacity"
          header-type="form-header"
        />
        <GrammarRadioButtonGroup
          :selected-value="`${lineOpacity}`"
          @input="onOpacityChange"
        >
          <GrammarRadioButtonItem
            label="25%"
            value="0.25"
          />
          <GrammarRadioButtonItem
            label="50%"
            value="0.5"
          />
          <GrammarRadioButtonItem
            label="75%"
            value="0.75"
          />
          <GrammarRadioButtonItem
            label="100%"
            value="1"
          />
        </GrammarRadioButtonGroup>
      </div>
    </GrammarOverlayCard>
  </div>
</template>
